import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_THRESHOLD_PX = 200;

/**
 * Frontend paging for lists that read newest-last and grow upward, e.g. a chat
 * transcript whose full history is already in memory.
 *
 * Mounts the tail of `items` and reveals another page whenever the scroll
 * container nears its top, restoring scroll position before paint so the
 * revealed page slots in above the current view instead of yanking it.
 *
 * @param {object}   params
 * @param {Array}    params.items             Full list, oldest first.
 * @param {object}   params.scrollElementRef  Ref to the scrollable element.
 * @param {number}   [params.pageSize]        Items revealed per page.
 * @param {number}   [params.thresholdPx]     Distance from top that triggers a reveal.
 * @param {string}   [params.resetKey]        Changing this pages back to the newest tail.
 * @param {boolean}  [params.enabled]         When false, every item is returned and no
 *                                            scroll listener is attached.
 * @returns {{ visibleItems: Array, hasOlderItems: boolean, loadOlderItems: () => void }}
 */
export const useReverseInfiniteScroll = ({
  items = [],
  scrollElementRef,
  pageSize = DEFAULT_PAGE_SIZE,
  thresholdPx = DEFAULT_THRESHOLD_PX,
  resetKey = '',
  enabled = true,
}) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  // Captured just before a page is revealed and consumed by the layout effect
  // below. Its presence also marks a reveal as in flight, which keeps one scroll
  // gesture from queueing several pages.
  const prependAnchorRef = useRef(null);

  useEffect(() => {
    setVisibleCount(pageSize);
    prependAnchorRef.current = null;
  }, [resetKey, pageSize]);

  const visibleItems = useMemo(() => {
    if (!enabled || visibleCount >= items.length) return items;
    return items.slice(items.length - visibleCount);
  }, [enabled, items, visibleCount]);

  // Also gates the scroll listener below, so a disabled hook observes nothing.
  const hasOlderItems = enabled && visibleCount < items.length;

  const loadOlderItems = useCallback(() => {
    const scrollEl = scrollElementRef?.current;
    if (scrollEl) {
      prependAnchorRef.current = {
        scrollHeight: scrollEl.scrollHeight,
        scrollTop: scrollEl.scrollTop,
      };
    }
    setVisibleCount((prev) => prev + pageSize);
  }, [scrollElementRef, pageSize]);

  // Layout effect, not a plain effect: the correction has to land before the
  // browser paints the taller list, otherwise the jump is visible.
  useLayoutEffect(() => {
    const anchor = prependAnchorRef.current;
    if (!anchor) return;
    prependAnchorRef.current = null;

    const scrollEl = scrollElementRef?.current;
    if (!scrollEl) return;
    scrollEl.scrollTop = anchor.scrollTop + (scrollEl.scrollHeight - anchor.scrollHeight);
  }, [visibleCount, scrollElementRef]);

  useEffect(() => {
    const scrollEl = scrollElementRef?.current;
    if (!scrollEl || !hasOlderItems) return undefined;

    const onScroll = () => {
      if (prependAnchorRef.current) return;
      if (scrollEl.scrollTop <= thresholdPx) loadOlderItems();
    };

    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', onScroll);
  }, [scrollElementRef, hasOlderItems, loadOlderItems, thresholdPx]);

  return { visibleItems, hasOlderItems, loadOlderItems };
};
